import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router";
import { WagmiProvider } from "wagmi";

import { AppShell } from "@/components/AppShell";
import { ErrorState } from "@/components/primitives";
import { ActivityPage, OrdersPage } from "@/routes/ActivityPage";
import { CatalogPage, ProductPage } from "@/routes/CatalogPage";
import { DashboardPage } from "@/routes/DashboardPage";
import { DevPage, NotFoundPage } from "@/routes/DevPage";
import { LandingPage } from "@/routes/LandingPage";
import { MembershipPage } from "@/routes/MembershipPage";
import { ProtocolPage } from "@/routes/ProtocolPage";
import { VouchersPage } from "@/routes/VouchersPage";
import { isUnconfigured, runtimeConfig } from "@/lib/runtime-config";
import { wagmiConfig } from "@/lib/wagmi";

/**
 * Retries are off.
 *
 * Every query here either reads the chain — where the server already rotates
 * through several endpoints before giving up — or reads the hub's own database,
 * where a failure is a bug rather than a blip. Retrying on top of that turns a
 * clear error into three slow ones and hides the endpoint rotation that already
 * happened.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const config = runtimeConfig();

  /*
    The shell never ran, so every address is the zero address and nothing
    on-chain can work. Almost always one specific mistake: browsing the Vite
    port instead of the Go port. Saying so beats rendering an app whose every
    read fails for reasons that look like a broken deployment.
  */
  if (isUnconfigured(config)) {
    return (
      <div className="sheet py-32">
        <ErrorState title="Konfigurasi runtime tidak ditemukan" code="NO_RUNTIME_CONFIG">
          Halaman ini dimuat tanpa shell Go, jadi alamat kontraknya kosong dan
          tidak ada yang dapat dibaca dari chain. Buka port Go (
          <code className="datum">http://127.0.0.1:3100</code>), bukan port Vite —
          Vite berjalan di belakangnya.
        </ErrorState>
      </div>
    );
  }

  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppShell>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/membership" element={<MembershipPage />} />
              <Route path="/vouchers" element={<VouchersPage />} />
              <Route path="/catalog" element={<CatalogPage />} />
              <Route path="/catalog/:slug" element={<ProductPage />} />
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/activity" element={<ActivityPage />} />
              <Route path="/protocol" element={<ProtocolPage />} />
              <Route path="/dev" element={<DevPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </AppShell>
        </BrowserRouter>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
